/**
 * TenantDevicesTab — SAAS-COMPANIES-P2-DEVICES-TAB.
 *
 * Aba "Dispositivos" do detalhe da empresa. Reaproveita getFleetMapDevices
 * ({tenantId}) — a MESMA fonte tenant-scoped do mapa (endpoint P2-A, ternária
 * platform-only) — então NÃO precisa de backend novo. Tabela + KPIs + busca +
 * deep-link ao DeviceDetail; sem mutações (read-only).
 */
import { useEffect, useMemo, useState } from "react";
import { getFleetMapDevices } from "../../lib/api.js";
import { deviceStatusFull } from "../fleet/DeviceMarker.jsx";

function Kpi({ label, value, tone }) {
  return (
    <div className="glass-card-pad">
      <div className={`text-2xl font-bold ${tone || ""}`}>{value}</div>
      <div className="text-xs text-noc-muted">{label}</div>
    </div>
  );
}

function fmtLastSeen(iso) {
  if (!iso) return "sem heartbeat";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`;
  return `${Math.floor(s / 86400)}d atrás`;
}

const STATUS_DOT = {
  offline: "bg-noc-muted", "relay-off": "bg-noc-warn",
  action: "bg-noc-accent", online: "bg-noc-ok",
};

export default function TenantDevicesTab({ tenantId, onNavigate }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr(null);
    getFleetMapDevices({ tenantId, signal: ac.signal })
      .then((d) => setDevices(d.devices || []))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha ao carregar dispositivos."); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [tenantId]);

  const hasGeo = (d) => typeof d.lat === "number" && typeof d.lng === "number";

  const kpi = useMemo(() => ({
    total: devices.length,
    online: devices.filter((d) => d.online).length,
    offline: devices.filter((d) => !d.online).length,
    protected: devices.filter((d) => d.effective_protected).length,
    noGeo: devices.filter((d) => !hasGeo(d)).length,
    noHb: devices.filter((d) => !d.last_seen).length,
  }), [devices]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      [d.device_id, d.customer_name, d.mac, d.fw_version]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [devices, query]);

  if (loading) return <div className="text-sm text-noc-muted glass-card-pad">Carregando dispositivos…</div>;
  if (err) return <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-3">{err}</div>;
  if (devices.length === 0) {
    return <div className="glass-card-pad text-sm text-noc-muted text-center py-10">
      Nenhum dispositivo nesta empresa ainda.
    </div>;
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <Kpi label="Total" value={kpi.total} />
        <Kpi label="Online" value={kpi.online} tone="text-noc-ok" />
        <Kpi label="Offline" value={kpi.offline} tone="text-noc-muted" />
        <Kpi label="Protegidos" value={kpi.protected} tone="text-noc-purple" />
        <Kpi label="Sem localização" value={kpi.noGeo} />
        <Kpi label="Sem heartbeat" value={kpi.noHb} tone="text-noc-warn" />
      </div>

      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Buscar device_id / cliente / MAC / firmware…"
        className="h-8 px-2.5 rounded-lg bg-noc-bg border border-noc-border text-xs text-noc-text w-full sm:w-72" />

      <div className="glass-card-pad overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-noc-muted text-xs border-b border-noc-border">
              <th className="py-2 pr-3">Device</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Firmware</th>
              <th className="py-2 pr-3">Última comunicação</th>
              <th className="py-2 pr-3">Cliente</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => {
              const st = deviceStatusFull(d);
              return (
                <tr key={d.device_id} className="border-b border-noc-border/50">
                  <td className="py-2 pr-3">
                    <span className="font-mono">{d.device_id}</span>
                    {d.effective_protected && <span className="ml-1 text-noc-purple">🔒</span>}
                    {!hasGeo(d) && <span className="ml-1 text-[10px] text-noc-muted">sem localização</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${STATUS_DOT[st.variant] || "bg-noc-muted"}`} />
                      <span className="text-xs text-noc-muted">{st.tooltip}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-noc-muted font-mono text-xs">{d.fw_version || "—"}</td>
                  <td className="py-2 pr-3 text-noc-muted text-xs">{fmtLastSeen(d.last_seen)}</td>
                  <td className="py-2 pr-3 text-noc-muted text-xs truncate max-w-[10rem]">{d.customer_name || "—"}</td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={() => onNavigate?.("detail", d.device_id)}
                      className="text-xs text-noc-accent border border-noc-accent/40 rounded px-2 py-1">
                      abrir detalhe
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div className="text-xs text-noc-muted py-3 text-center">
            Nenhum dispositivo para “{query.trim()}”.
          </div>
        )}
      </div>
    </div>
  );
}
